import { Activity, AlertCircle, CheckCircle2, ClipboardCopy, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import {
  getApplicationHealthIncidents,
  updateApplicationHealthIncidentStatus,
  type ApplicationHealthIncident,
  type ApplicationHealthIncidentFilters,
  type ApplicationHealthIncidentStatus,
  type ApplicationHealthReasonCode
} from '../lib/api/applicationHealth'
import { buildApplicationHealthReport } from '../lib/applicationHealthReport'
import { cn } from '../utils/cn'

const reasonLabels: Record<ApplicationHealthReasonCode, string> = {
  data_store_failure: 'Data store failure',
  dependency_failure: 'Dependency failure',
  timeout: 'Timeout',
  server_error_response: 'Server error response',
  unhandled_exception: 'Unhandled exception'
}

const statusStyles: Record<ApplicationHealthIncidentStatus, string> = {
  New: 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-500/40 dark:bg-rose-950/30 dark:text-rose-200',
  Acknowledged: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-200',
  Resolved: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-950/30 dark:text-emerald-200'
}

const formatDateTime = (value: string) => new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'medium'
}).format(new Date(value))

export const ApplicationHealthPage = () => {
  const [filters, setFilters] = useState<ApplicationHealthIncidentFilters>({
    status: 'active',
    limit: 100
  })
  const [items, setItems] = useState<ApplicationHealthIncident[]>([])
  const [newCount, setNewCount] = useState(0)
  const [acknowledgedCount, setAcknowledgedCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [httpStatusInput, setHttpStatusInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [updatingReference, setUpdatingReference] = useState<string | null>(null)
  const [acknowledgingAll, setAcknowledgingAll] = useState(false)
  const [copyingReference, setCopyingReference] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const loadRequest = useRef(0)

  const loadIncidents = useCallback(async (refresh = false) => {
    const request = ++loadRequest.current
    if (refresh) {
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)
    setNotice(null)
    try {
      const result = await getApplicationHealthIncidents(filters)
      if (request !== loadRequest.current) return false
      setItems(result.items)
      setNewCount(result.newCount)
      setAcknowledgedCount(result.acknowledgedCount)
      setHasMore(result.hasMore)
      return true
    } catch (loadError) {
      if (request !== loadRequest.current) return false
      console.error('Failed to load application health incidents', loadError)
      setItems([])
      setHasMore(false)
      setError('Could not load application health incidents right now.')
      return false
    } finally {
      if (request === loadRequest.current) {
        setLoading(false)
        setRefreshing(false)
      }
    }
  }, [filters])

  useEffect(() => {
    void loadIncidents()
    return () => { loadRequest.current += 1 }
  }, [loadIncidents])

  const updating = updatingReference !== null || acknowledgingAll
  const busy = loading || refreshing || updating
  const newItems = items.filter((incident) => incident.status === 'New')

  const changeStatus = async (
    incident: ApplicationHealthIncident,
    nextStatus: ApplicationHealthIncidentStatus
  ) => {
    if (busy) return
    setUpdatingReference(incident.correlationReference)
    setError(null)
    setNotice(null)
    try {
      await updateApplicationHealthIncidentStatus(incident.correlationReference, nextStatus)
      await loadIncidents(true)
    } catch (updateError) {
      console.error('Failed to update application health incident', updateError)
      setError('Could not update that incident.')
    } finally {
      setUpdatingReference(null)
    }
  }

  const acknowledgeAll = async () => {
    if (busy || newItems.length === 0) return
    setAcknowledgingAll(true)
    setError(null)
    setNotice(null)
    let acknowledged = 0
    try {
      for (const incident of newItems) {
        try {
          const updated = await updateApplicationHealthIncidentStatus(incident.correlationReference, 'Acknowledged')
          if (updated.status === 'Acknowledged') acknowledged += 1
        } catch (updateError) {
          console.error('Failed to acknowledge application health incident', updateError)
        }
      }
      const reloaded = await loadIncidents(true)
      if (acknowledged !== newItems.length) {
        setError(`Acknowledged ${acknowledged} of ${newItems.length} new incidents. ${newItems.length - acknowledged} could not be acknowledged. ${reloaded ? 'Retry the remaining new incidents.' : 'Could not refresh the inbox; refresh before retrying.'}`)
      } else {
        setNotice(`Acknowledged ${acknowledged} incident${acknowledged === 1 ? '' : 's'}.`)
      }
    } finally {
      setAcknowledgingAll(false)
    }
  }

  const copyIncidents = async (incident?: ApplicationHealthIncident) => {
    if (busy || copyingReference !== null || items.length === 0) return
    const copiedItems = incident ? [incident] : items
    setCopyingReference(incident?.correlationReference ?? 'all')
    setError(null)
    setNotice(null)
    try {
      await navigator.clipboard.writeText(buildApplicationHealthReport(copiedItems, {
        scope: incident ? 'single' : 'displayed',
        filters,
        hasMore
      }))
      setNotice(`Copied investigation details for ${copiedItems.length} incident${copiedItems.length === 1 ? '' : 's'} to the clipboard.`)
    } catch (copyError) {
      console.error('Failed to copy application health incidents', copyError)
      setError('Could not copy to the clipboard. Check clipboard permissions and try again.')
    } finally {
      setCopyingReference(null)
    }
  }

  const activeCount = newCount + acknowledgedCount

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">System &amp; Billing</p>
          <h1 className="text-3xl font-semibold text-foreground">Application Health</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Server-owned HTTP failure incidents. Sentry and alerting wake the team; this inbox is the durable
            acknowledgment and investigation record.
          </p>
        </div>
        <Button variant="outline" onClick={() => void loadIncidents(true)} disabled={busy}>
          <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
          Refresh
        </Button>
      </header>

      <section className="grid gap-3 md:grid-cols-3" aria-label="Current application health">
        <HealthSummary
          icon={Activity}
          label="Currently active"
          value={activeCount}
          description="New or acknowledged failures"
          urgent={activeCount > 0}
        />
        <HealthSummary
          icon={TriangleAlert}
          label="Needs acknowledgment"
          value={newCount}
          description="No administrator has claimed these"
          urgent={newCount > 0}
        />
        <HealthSummary
          icon={ShieldCheck}
          label="Acknowledged"
          value={acknowledgedCount}
          description="Known and under investigation"
        />
      </section>

      <section className="rounded-xl border border-border bg-card p-4">
        <fieldset disabled={updating} className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <FilterSelect
            label="Status"
            value={filters.status ?? 'active'}
            onChange={(value) => setFilters((current) => ({
              ...current,
              status: value as ApplicationHealthIncidentFilters['status']
            }))}
            options={[
              ['active', 'Active'],
              ['new', 'New'],
              ['acknowledged', 'Acknowledged'],
              ['resolved', 'Resolved'],
              ['all', 'All']
            ]}
          />
          <FilterSelect
            label="Reason"
            value={filters.reasonCode ?? ''}
            onChange={(value) => setFilters((current) => ({
              ...current,
              reasonCode: (value || undefined) as ApplicationHealthReasonCode | undefined
            }))}
            options={[
              ['', 'All reasons'],
              ...Object.entries(reasonLabels)
            ]}
          />
          <FilterSelect
            label="Method"
            value={filters.httpMethod ?? ''}
            onChange={(value) => setFilters((current) => ({ ...current, httpMethod: value || undefined }))}
            options={[
              ['', 'All methods'],
              ['GET', 'GET'],
              ['POST', 'POST'],
              ['PUT', 'PUT'],
              ['PATCH', 'PATCH'],
              ['DELETE', 'DELETE'],
              ['OTHER', 'OTHER']
            ]}
          />
          <label className="space-y-1 text-sm font-semibold text-foreground">
            HTTP status
            <input
              type="number"
              min={500}
              max={599}
              value={httpStatusInput}
              onChange={(event) => {
                const value = event.target.value
                setHttpStatusInput(value)
                if (!value || /^5\d{2}$/.test(value)) {
                  setFilters((current) => ({
                    ...current,
                    httpStatus: value ? Number(value) : undefined
                  }))
                }
              }}
              placeholder="All 5xx"
              className="h-10 w-full rounded-lg border border-border bg-background px-3 font-normal outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <label className="space-y-1 text-sm font-semibold text-foreground">
            Route template
            <input
              value={filters.routeTemplate ?? ''}
              onChange={(event) => setFilters((current) => ({
                ...current,
                routeTemplate: event.target.value || undefined
              }))}
              placeholder="api/mobile/..."
              className="h-10 w-full rounded-lg border border-border bg-background px-3 font-normal outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
        </fieldset>
        <p className="mt-3 text-xs text-muted-foreground">
          Only bounded reason codes, HTTP metadata, route templates, timestamps, counts, and correlation references are retained.
          Request and exception details are never stored.
        </p>
      </section>

      <section
        aria-label="Bulk incident actions"
        className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="text-sm text-muted-foreground">
          <p>Bulk actions apply to the {items.length} displayed incidents. Only new incidents are acknowledged.</p>
          {hasMore && <p className="mt-1 font-semibold">More incidents match these filters; only the latest {items.length} are shown and included.</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={busy || copyingReference !== null || items.length === 0}
            onClick={() => void copyIncidents()}
          >
            <ClipboardCopy className="mr-2 h-4 w-4" />
            {copyingReference === 'all' ? 'Copying…' : 'Copy to clipboard'}
          </Button>
          <Button disabled={busy || newItems.length === 0} onClick={() => void acknowledgeAll()}>
            <ShieldCheck className="mr-2 h-4 w-4" />
            {acknowledgingAll ? 'Acknowledging…' : 'Acknowledge all'}
          </Button>
        </div>
      </section>

      {notice && <p role="status" className="text-sm font-semibold text-foreground">{notice}</p>}

      {error && (
        <Card className="border border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-950/20">
          <CardContent className="flex items-start gap-3 py-4 text-rose-900 dark:text-rose-100">
            <AlertCircle className="mt-0.5 h-5 w-5" />
            <p role="alert" className="text-sm font-semibold">{error}</p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Card><CardContent className="py-8 text-sm text-muted-foreground">Loading application health incidents…</CardContent></Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={CheckCircle2}
              title="No matching application failures"
              description="No server-owned HTTP incidents match the current filters."
            />
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px] text-left text-sm">
              <thead className="border-b border-border bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Where</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3 text-right">Occurrences</th>
                  <th className="px-4 py-3">Last seen</th>
                  <th className="px-4 py-3">Correlation</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((incident) => (
                  <IncidentRow
                    key={incident.correlationReference}
                    incident={incident}
                    updating={busy}
                    copying={copyingReference === incident.correlationReference}
                    copyDisabled={busy || copyingReference !== null}
                    onCopy={() => void copyIncidents(incident)}
                    onChangeStatus={(status) => void changeStatus(incident, status)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

const HealthSummary = ({
  icon: Icon,
  label,
  value,
  description,
  urgent = false
}: {
  icon: typeof Activity
  label: string
  value: number
  description: string
  urgent?: boolean
}) => (
  <Card className={cn('border border-border', urgent && 'border-rose-300 dark:border-rose-500/40')}>
    <CardContent className="flex items-center gap-4 py-4">
      <div className={cn(
        'rounded-xl bg-muted p-3 text-muted-foreground',
        urgent && 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-200'
      )}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-sm font-semibold text-muted-foreground">{label}</p>
        <p className="text-3xl font-bold text-foreground">{value}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </CardContent>
  </Card>
)

const FilterSelect = ({
  label,
  value,
  onChange,
  options
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: string[][]
}) => (
  <label className="space-y-1 text-sm font-semibold text-foreground">
    {label}
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-lg border border-border bg-background px-3 font-normal outline-none focus:ring-2 focus:ring-ring"
    >
      {options.map(([optionValue, optionLabel]) => (
        <option key={optionValue} value={optionValue}>{optionLabel}</option>
      ))}
    </select>
  </label>
)

const IncidentRow = ({
  incident,
  updating,
  copying,
  copyDisabled,
  onCopy,
  onChangeStatus
}: {
  incident: ApplicationHealthIncident
  updating: boolean
  copying: boolean
  copyDisabled: boolean
  onCopy: () => void
  onChangeStatus: (status: ApplicationHealthIncidentStatus) => void
}) => (
  <tr className="align-top">
    <td className="px-4 py-4">
      <span className={cn('inline-flex rounded-full border px-2.5 py-1 text-xs font-bold', statusStyles[incident.status])}>
        {incident.status}
      </span>
    </td>
    <td className="px-4 py-4">
      <p className="font-mono text-xs font-semibold text-foreground">{incident.routeTemplate}</p>
      <p className="mt-1 text-xs text-muted-foreground">{incident.httpMethod} · HTTP {incident.httpStatus}</p>
    </td>
    <td className="px-4 py-4 font-medium text-foreground">{reasonLabels[incident.reasonCode]}</td>
    <td className="px-4 py-4 text-right text-lg font-bold text-foreground">{incident.occurrenceCount}</td>
    <td className="px-4 py-4">
      <p className="font-medium text-foreground">{formatDateTime(incident.lastSeenAt)}</p>
      <p className="mt-1 text-xs text-muted-foreground">First {formatDateTime(incident.firstSeenAt)}</p>
    </td>
    <td className="max-w-48 px-4 py-4">
      <span className="block truncate font-mono text-xs text-muted-foreground" title={incident.correlationReference}>
        {incident.correlationReference}
      </span>
    </td>
    <td className="px-4 py-4">
      <div className="flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" disabled={copyDisabled} onClick={onCopy}>
          <ClipboardCopy className="mr-2 h-4 w-4" />
          {copying ? 'Copying…' : 'Copy to clipboard'}
        </Button>
        {incident.status === 'New' && (
          <Button size="sm" variant="outline" disabled={updating} onClick={() => onChangeStatus('Acknowledged')}>
            Acknowledge
          </Button>
        )}
        {incident.status !== 'Resolved' && (
          <Button size="sm" variant="secondary" disabled={updating} onClick={() => onChangeStatus('Resolved')}>
            Resolve
          </Button>
        )}
        {incident.status !== 'New' && (
          <Button size="sm" variant="ghost" disabled={updating} onClick={() => onChangeStatus('New')}>
            Reopen
          </Button>
        )}
      </div>
    </td>
  </tr>
)
